import { Auth } from "aws-amplify";
import React, { createContext, useContext, useEffect, useState } from "react";

export interface AskPCOSUserType {
  user_sub: string;
  email: string;
  firstName?: string;
  // Add other user properties as needed
}

interface UserProfileContextType {
  userProfile: AskPCOSUserType | null;
  isLoading: boolean;
  error: string | null;
  refreshUserProfile: () => Promise<void>;
}

const UserProfileContext = createContext<UserProfileContextType | undefined>(
  undefined
);

export const UserProfileProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [userProfile, setUserProfile] = useState<AskPCOSUserType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshUserProfile = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const user = await Auth.currentAuthenticatedUser();

      if (user) {
        setUserProfile({
          user_sub: user.attributes.sub,
          email: user.attributes.email,
          firstName:
            user.attributes.given_name || user.attributes.name || undefined,
          // Map other attributes as needed
        });
      } else {
        setUserProfile(null);
      }
    } catch (err) {
      console.error("Error refreshing user profile:", err);
      setUserProfile(null);
      setError("Failed to load user profile");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refreshUserProfile();
  }, []);

  return (
    <UserProfileContext.Provider
      value={{
        userProfile,
        isLoading,
        error,
        refreshUserProfile,
      }}
    >
      {children}
    </UserProfileContext.Provider>
  );
};

export const useUserProfile = (): UserProfileContextType => {
  const context = useContext(UserProfileContext);
  if (context === undefined) {
    throw new Error("useUserProfile must be used within a UserProfileProvider");
  }
  return context;
};
